import styled, { css, keyframes } from 'styled-components'
import { Border, Colors, Shadows, Spacing, Typography } from '../../styles'
import { TIconColor, TTextColor } from '../../styles/colors'
import { OVERLAY_MAX_WIDTH, TASK_HEIGHT_TOUCH, pointerQuery } from '../../styles/dimensions'
import { Motion } from '../../styles/motion'
import { TIconType } from '../atoms/Icon'

export const MENU_WIDTH = '192px'
const MENU_LABEL_WIDTH = '100px'

export const MenuTriggerShared = css<{ $unstyled?: boolean }>`
    all: unset;
    width: 100%;
    height: 100%;
    ${({ $unstyled }) =>
        !$unstyled &&
        `
        border-radius: ${Border.radius.medium};
        &:focus {
            outline: ${Border.stroke.small} solid ${Colors.background.border};
        }
    `}
`
export const MenuItemShared = css<{ $textColor?: TTextColor; $disabled?: boolean; $selected?: boolean }>`
    display: flex;
    align-items: center;
    gap: ${Spacing._12};
    width: ${MENU_WIDTH};
    flex: 1;
    margin: ${Spacing._4} 0;
    padding: ${Spacing._4} ${Spacing._12};
    outline: none;
    border-radius: ${Border.radius.small};
    /* Menus are the touch path for actions that are drag-only on desktop, so rows meet hit.touch on phones. */
    ${pointerQuery.noHover} {
        min-height: ${TASK_HEIGHT_TOUCH};
        box-sizing: border-box;
    }
    ${({ $textColor }) => $textColor && `color: ${Colors.text[$textColor]};`}
    ${({ $disabled }) => !$disabled && `cursor: pointer;`}
    ${({ $selected }) => $selected && `background-color: ${Colors.background.sub};`}
    &[data-highlighted] {
        ${({ $disabled }) =>
            !$disabled &&
            `
        outline: ${Border.stroke.small} solid ${Colors.background.border};
        background-color: ${Colors.background.sub};
        `}
    }
    &[data-state='open'] {
        outline: ${Border.stroke.small} solid ${Colors.background.border};
        background-color: ${Colors.background.sub};
    }
    ${Typography.body.medium};
`
// `translate` and `scale` are separate from `transform`, so a surface that centres itself with
// `transform` (GTDialog) keeps its position while it animates.
const overlayIn = keyframes`
    from {
        opacity: 0;
        translate: var(--overlay-from-x, 0) var(--overlay-from-y, 0);
        scale: ${Motion.overlayScale};
    }
`
const overlayOut = keyframes`
    to {
        opacity: 0;
        scale: ${Motion.overlayScale};
    }
`
export const MenuContentShared = css`
    /* Arrive from the trigger's side; Radix keeps the surface mounted until the exit animation ends. */
    animation: ${overlayIn} ${Motion.enter};
    &[data-side='top'] {
        --overlay-from-y: ${Motion.overlayOffset};
    }
    &[data-side='bottom'] {
        --overlay-from-y: -${Motion.overlayOffset};
    }
    &[data-side='left'] {
        --overlay-from-x: ${Motion.overlayOffset};
    }
    &[data-side='right'] {
        --overlay-from-x: -${Motion.overlayOffset};
    }
    &[data-state='closed'] {
        animation: ${overlayOut} ${Motion.exit} forwards;
    }
    z-index: 300;
    ${Typography.body.medium};
    padding: ${Spacing._4};
    background-color: ${Colors.background.white};
    border-radius: ${Border.radius.medium};
    /* Hairline drawn as a shadow ring rather than a border so it adds nothing to the clamped width below. */
    box-shadow: 0 0 0 ${Border.stroke.medium} ${Colors.background.border}, ${Shadows.modal};
    /* Baseline so no anchored surface can be wider than the screen. Radix's collision handling
       only shifts content back on screen; it never shrinks it. Left as content-box to keep desktop
       sizing identical, so surfaces with roomy padding subtract it from the clamp themselves. */
    max-width: ${OVERLAY_MAX_WIDTH};
`
export const MarginLeftIcon = styled.div`
    margin-left: auto;
`
export const MenuItemIconColumn = styled.div`
    width: ${Spacing._16};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`
export const FixedSizeIcon = styled.div<{ visible?: boolean }>`
    opacity: ${({ visible }) => (visible ? 1 : 0)};
`
export const MenuItemLabel = styled.span`
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: ${MENU_LABEL_WIDTH};
`

export interface GTMenuItem {
    label: string
    onClick?: () => void
    icon?: TIconType
    iconColor?: TIconColor // should take priority over iconColorHex
    iconColorHex?: string
    textColor?: TTextColor
    selected?: boolean
    hideCheckmark?: boolean
    disabled?: boolean
    subItems?: GTMenuItem[]
    renderer?: () => JSX.Element // override how the option is rendered
    count?: number
    keepOpenOnSelect?: boolean
    tip?: string
}
